/**
 * Widgets visuais das super páginas de BAR (/bar/{handle}).
 * Ficha com ícones (só campos existentes), destaques/dicas em cards e FAQ em acordeão.
 * 100% client-side. Modelado em AtracaoWidgets.
 * O CTA "Quer explorar mais?" (NavegacaoPilar) é renderizado genericamente pelo
 * EntityDetailPage (via PILAR_NAV), então NÃO fica aqui — evita duplicação.
 */

/** Ficha rápida visual — cards com ícone para os dados do bar. */
export function BarFicha({ entity }: { entity: any }) {
  const e = entity;
  const items = [
    e.vilarejoName ? { icon: '📍', label: 'Onde fica', value: e.vilarejoName } : null,
    e.tipoLabel ? { icon: '🍽️', label: 'Tipo', value: e.tipoLabel } : null,
    e.musicaAoVivo ? { icon: '🎵', label: 'Música ao vivo', value: 'Sim' } : null,
    e.horarios ? { icon: '🕐', label: 'Horários', value: e.horarios } : null,
    e.faixaPreco ? { icon: '💰', label: 'Preço', value: e.faixaPreco } : null,
    e.formasPagamento ? { icon: '💳', label: 'Pagamento', value: e.formasPagamento } : null,
    e.rating ? { icon: '⭐', label: 'Avaliação', value: e.rating } : null,
  ].filter(Boolean) as { icon: string; label: string; value: string }[];

  if (items.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <h2 className="font-display text-lg text-warm-dark mb-4 tracking-wide">Ficha rápida</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map(it => (
          <div key={it.label} className="flex items-start gap-3 p-3 rounded-xl bg-sand-50 border border-sand-100">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">{it.icon}</span>
            <div className="min-w-0">
              <p className="font-body text-[11px] uppercase tracking-wide text-warm-gray">{it.label}</p>
              <p className="font-body text-sm font-semibold text-warm-dark leading-snug">{it.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Destaques em cards visuais. */
export function BarDestaques({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 font-display text-xl text-warm-dark mb-4 tracking-wide">
        ✨ Destaques
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((h, i) => (
          <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-teal-light/5 border border-teal-light/20">
            <span className="text-teal-deep font-bold flex-shrink-0 mt-0.5">◆</span>
            <p className="font-body text-sm text-warm-gray leading-relaxed">{h}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dicas em cards. */
export function BarDicas({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 font-display text-xl text-warm-dark mb-4 tracking-wide">
        💡 Dicas de quem conhece
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((tip, i) => (
          <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-sand-200 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
            <span className="text-coral flex-shrink-0 mt-0.5">✓</span>
            <p className="font-body text-sm text-warm-gray leading-relaxed">{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** FAQ em acordeão + JSON-LD FAQPage. */
export function BarFAQ({ entity }: { entity: any }) {
  const faq = entity?.faq;
  if (!faq || faq.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f: any) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <div id="faq">
      <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">Perguntas frequentes</h2>
      <div className="space-y-3">
        {faq.map((f: any, i: number) => (
          <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
              <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
              <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
            </summary>
            <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </div>
  );
}

/** Composição visual completa das super páginas de bar. */
export function BarVisualSections({ entity }: { entity: any }) {
  return (
    <>
      <BarFicha entity={entity} />
      <BarDestaques items={entity.highlights} />
      <BarDicas items={entity.dicas} />
      <BarFAQ entity={entity} />
    </>
  );
}
